/**
 * Data access for the cart feature — server-side pricing + multi-item order creation.
 *
 * - ``POST /api/cart/quote/`` — re-prices the selection (no side effects). The client sends
 *   only ids + quantities; the server returns the authoritative subtotal/total + per-line
 *   breakdown for the bottom panel (ТЗ §10).
 * - ``POST /api/orders/`` with ``items`` — atomically creates one order for the whole
 *   selection, freezing prices server-side. Idempotent via the ``Idempotency-Key`` header.
 *
 * Types mirror ``apps.orders.serializers`` (``CartQuoteSerializer`` / ``CartQuoteLine`` /
 * ``CartOrderInputSerializer``). All money fields are DRF ``Decimal`` strings — never parsed
 * for economic decisions.
 */
import { apiClient } from "@shared/api";
import type { OrderDetail } from "@entities/order";

/** An untrusted cart line — just an id and a quantity. */
export interface CartItemInput {
  readonly product_id: number;
  readonly qty: number;
}

/** Body for ``POST /api/cart/quote/``. */
export interface CartQuoteInput {
  readonly items: readonly CartItemInput[];
  readonly star_glitter_spend?: number;
  readonly primogem_spend?: number;
}

/** Body for ``POST /api/orders/`` (cart path). */
export interface CartOrderInput extends CartQuoteInput {
  readonly user_comment?: string;
  /** Customer requests a payment receipt at checkout (ТЗ №2доп §16-18). */
  readonly needs_receipt?: boolean;
}

/** One priced line of a cart quote. */
export interface CartQuoteLine {
  readonly product_id: number;
  readonly title: string;
  readonly qty: number;
  readonly unit_price: string;
  readonly line_total: string;
  /** Effective primogem cap in RUB for this line; ``null`` = global fallback (ТЗ §5). */
  readonly primogem_cap_rub: string | null;
  /** Per-order quantity limit for the product (ТЗ §6). */
  readonly max_qty: number;
}

/**
 * Server-computed price preview for the whole selection (ТЗ §5).
 *
 * The backend is the single source of truth: all amounts (subtotal / applied currency /
 * total) and the user's spendable balances come from here — the checkout screen renders them
 * verbatim and never sums anything itself. ``total`` / ``*_applied_rub`` are the ТЗ §5 names;
 * the older ``total_rub`` / ``*_discount_rub`` aliases are kept for existing callers.
 */
export interface CartQuote {
  readonly subtotal: string;
  readonly discount_total: string;
  readonly primogem_discount_rub: string;
  readonly star_glitter_discount_rub: string;
  /** ТЗ §5 alias of ``star_glitter_discount_rub`` (RUB covered by star glitter). */
  readonly star_glitter_applied_rub: string;
  /** ТЗ §5 alias of ``primogem_discount_rub`` (RUB covered by primogems). */
  readonly primogem_applied_rub: string;
  readonly total_rub: string;
  /** ТЗ §5 alias of ``total_rub`` (final amount to pay). */
  readonly total: string;
  readonly max_primogem_rub: string;
  readonly max_star_glitter_rub: string;
  /** The user's spendable star-glitter balance (integer units). */
  readonly available_star_glitter: number;
  /** The user's spendable primogem balance (integer units). */
  readonly available_primogems: number;
  readonly lines: readonly CartQuoteLine[];
}

export const CART_QUOTE_PATH = "/cart/quote/";
export const ORDERS_PATH = "/orders/";

/** Request a server-side price preview for the current selection (no side effects). */
export function quoteCart(input: CartQuoteInput): Promise<CartQuote> {
  return apiClient.post<CartQuote, CartQuoteInput>(CART_QUOTE_PATH, {
    body: input,
  });
}

/**
 * Create one order for the whole selection. The ``idempotencyKey`` makes a retried or
 * double-tapped checkout safe (the server returns the already-created order).
 */
export function createCartOrder(
  input: CartOrderInput,
  idempotencyKey: string,
): Promise<OrderDetail> {
  return apiClient.post<OrderDetail, CartOrderInput>(ORDERS_PATH, {
    body: input,
    idempotencyKey,
  });
}
